/**
 * Stripe.js, loaded on demand.
 *
 * NOT A BUNDLED DEPENDENCY. Stripe requires its script to be served from
 * js.stripe.com — a vendored copy breaks PCI scope and stops receiving their
 * fraud and 3DS updates — so it is a tag, added the first time somebody opens
 * the billing screen. A producer who never buys anything never loads it.
 *
 * The promise is cached, so opening the screen twice does not add two tags.
 */
declare global {
  interface Window {
    Stripe?: (key: string) => StripeInstance;
  }
}

/** Only the surface this app uses. The full SDK types are a dependency we do
 *  not otherwise need, and a narrow local shape makes the seam obvious. */
export interface StripeInstance {
  confirmPayment(options: {
    elements: StripeElements;
    confirmParams?: { return_url?: string };
    redirect?: 'if_required';
  }): Promise<{ error?: { message?: string } }>;
  elements(options: { clientSecret: string; appearance?: unknown }): StripeElements;
}

export interface StripeElements {
  create(type: 'payment', options?: unknown): StripeElement;
  getElement(type: 'payment'): StripeElement | null;
}

export interface StripeElement {
  mount(target: HTMLElement): void;
  unmount(): void;
}

const SRC = 'https://js.stripe.com/v3/';

let loading: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.Stripe) return Promise.resolve();

  loading ??= new Promise<void>((resolve, reject) => {
    // A tag may already be present from a previous mount that has not finished.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SRC}"]`);

    if (existing) {
      existing.addEventListener('load', () => {
        resolve();
      });
      existing.addEventListener('error', () => {
        reject(new Error('Stripe could not be loaded'));
      });
      return;
    }

    const tag = document.createElement('script');
    tag.src = SRC;
    tag.async = true;
    tag.addEventListener('load', () => {
      resolve();
    });
    tag.addEventListener('error', () => {
      // Reset so a retry can try again rather than resolving a dead promise
      // for the life of the page — an ad blocker is the usual cause and the
      // producer may well turn it off and press the button again.
      loading = null;
      reject(new Error('Stripe could not be loaded'));
    });
    document.head.appendChild(tag);
  });

  return loading;
}

/**
 * The publishable key comes from the SERVER, per purchase, rather than from a
 * build-time variable: one image is deployed to several environments, and the
 * key differs between them. It is publishable by definition — it is the key
 * that is meant to be public — so travelling in a response is fine.
 */
export async function loadStripe(publishableKey: string): Promise<StripeInstance> {
  await loadScript();

  const factory = window.Stripe;
  if (!factory) throw new Error('Stripe could not be loaded');

  return factory(publishableKey);
}
